/**
 * HeaderBrand — drop-in brand identity block for every page header.
 *
 * Renders:
 *   [BrandLogo  Air Compressor Canada]  [Screen share]  [My company]
 *   └── click area → "/" (Home) ─────┘
 *
 * Designed to slot into the LEFT side of every page's existing
 * sticky/flat header so there's exactly ONE place that owns the
 * brand identity. No more floating overlays.
 *
 * Hide rules (matches the previous floating widget):
 *   • Admins / super-admins → only the brand (no pills).
 *   • Screen-share pill hidden on /conversations-v2.
 *   • My-Company pill hidden when the user is already at the customers
 *     list for their company, or (for customer-role users) on
 *     /my-account.
 *   • Whole component renders nothing on auth screens (/login,
 *     /signup, /customer-signup, /conv-link/*).
 */
import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, Link } from "react-router-dom";
import { createPortal } from "react-dom";
import { Monitor, Buildings, X, CaretRight, Question } from "@phosphor-icons/react";
import api from "@/lib/api";
import BrandLogo from "@/components/BrandLogo";
import { useBranding } from "@/lib/branding";
import { useT, useI18n } from "@/lib/i18n";

const HIDDEN_ROUTES = ["/login", "/signup", "/customer-signup", "/conv-link"];
const SCREEN_SHARE_ROUTES = ["/conversations-v2"];

function _customerListPath(country) {
  const c = String(country || "").trim().toUpperCase();
  if (c === "USA" || c === "US" || c === "UNITED STATES") return "/customers-us";
  if (c === "CANADA" || c === "CA") return "/customers-ca";
  return "/customers-other";
}

function _companyHref(company, role) {
  if (role === "customer") return "/my-account";
  if (!company?.customer_id) return null;
  return `${_customerListPath(company.country)}?focus=${encodeURIComponent(company.customer_id)}`;
}

function Pill({ onClick, label, Icon, testid, color = "slate" }) {
  const tw = {
    slate:   "bg-white text-slate-700 border-slate-200 hover:bg-slate-50",
    emerald: "bg-white text-emerald-700 border-emerald-200 hover:bg-emerald-50",
  };
  return (
    <button type="button" onClick={onClick} data-testid={testid} title={label}
      className={`inline-flex items-center gap-1 h-7 px-2 rounded-sm border text-[10px] font-bold uppercase tracking-wider transition-colors ${tw[color]}`}>
      <Icon size={11} weight="bold" />
      <span className="hidden md:inline">{label}</span>
    </button>
  );
}

function CompanyPickerModal({ companies, onPick, onClose }) {
  return createPortal(
    <div className="fixed inset-0 z-[70] flex items-center justify-center p-3"
         data-testid="company-picker-overlay">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative w-full max-w-md bg-white rounded-sm shadow-xl border border-slate-200 overflow-hidden flex flex-col max-h-[80vh]">
        <header className="px-4 py-3 border-b border-slate-200 flex items-center gap-3">
          <Buildings size={16} weight="bold" className="text-emerald-700" />
          <h2 className="text-sm font-bold text-slate-900 flex-1">Pick a company</h2>
          <button onClick={onClose} data-testid="company-picker-close"
                  className="w-8 h-8 flex items-center justify-center rounded-sm hover:bg-slate-100 text-slate-500">
            <X size={16} />
          </button>
        </header>
        <p className="px-4 py-2 text-[12px] text-slate-600 border-b border-slate-100">
          You&apos;re linked to multiple companies. Which one do you want to open?
        </p>
        <ul className="overflow-y-auto divide-y divide-slate-100">
          {companies.map((c) => (
            <li key={c.customer_id}>
              <button data-testid={`company-pick-${c.customer_id}`}
                      onClick={() => onPick(c)}
                      className="w-full px-4 py-3 flex items-center gap-3 text-left hover:bg-slate-50">
                <div className="flex-1 min-w-0">
                  <div className="text-[13px] font-semibold text-slate-900 truncate">
                    {c.company_name || c.customer_id}
                  </div>
                  {c.country && (
                    <div className="text-[10px] text-slate-500 uppercase tracking-wider">
                      {c.country}
                    </div>
                  )}
                </div>
                <CaretRight size={14} className="text-slate-400" />
              </button>
            </li>
          ))}
        </ul>
      </div>
    </div>,
    document.body,
  );
}

export default function HeaderBrand({ user, compact = false, showBrand = true }) {
  const location = useLocation();
  const navigate = useNavigate();
  const branding = useBranding();
  const t = useT();

  const [companies, setCompanies] = useState(null);
  const [role, setRole] = useState(null);
  const [picker, setPicker] = useState(false);

  const isAdmin = !!(user?.is_admin || user?.is_super_admin);
  const isCustomer = user?.role === "customer";
  const onHiddenRoute = HIDDEN_ROUTES.some((p) => location.pathname.startsWith(p));

  useEffect(() => {
    if (!user || isAdmin || isCustomer) {
      setCompanies(null);
      return;
    }
    let cancelled = false;
    api.get("/auth/my-companies")
      .then((r) => {
        if (cancelled) return;
        setCompanies(r.data?.companies || []);
        setRole(r.data?.role || null);
      })
      .catch(() => { if (!cancelled) setCompanies([]); });
    return () => { cancelled = true; };
  }, [user?.email, isAdmin, isCustomer]); // eslint-disable-line react-hooks/exhaustive-deps

  const list = useMemo(() => Array.isArray(companies) ? companies : [], [companies]);

  const showScreenShare = useMemo(() => {
    if (isAdmin) return false;
    return !SCREEN_SHARE_ROUTES.some((p) => location.pathname.startsWith(p));
  }, [isAdmin, location.pathname]);

  const showCompanyBtn = useMemo(() => {
    if (isAdmin) return false;
    if (isCustomer) return !location.pathname.startsWith("/my-account");
    if (list.length === 0) return false;
    if (list.length === 1) {
      const dest = _customerListPath(list[0].country);
      return !location.pathname.startsWith(dest);
    }
    return true;
  }, [isAdmin, isCustomer, list, location.pathname]);

  if (!user || onHiddenRoute) return null;

  function onCompanyClick() {
    if (isCustomer) { navigate("/my-account"); return; }
    if (list.length === 1) {
      const href = _companyHref(list[0], role);
      if (href) navigate(href);
      return;
    }
    if (list.length > 1) setPicker(true);
  }

  function onPick(c) {
    setPicker(false);
    const href = _companyHref(c, role);
    if (href) navigate(href);
  }

  const brandName  = branding.title || t("brand.name", "Compressors R Us");
  const companyLabel = isCustomer
    ? "My account"
    : (list.length === 1 ? (list[0].company_name || "My company") : "My Companies");

  return (
    <div className="flex items-center gap-1.5 sm:gap-2 min-w-0 shrink-0"
         data-testid="header-brand">
      {showBrand && (
        <Link to="/" data-testid="header-brand-home"
              className="flex items-center gap-1.5 min-w-0 hover:text-blue-700 transition-colors">
          {/* iter150AZ.43 — logo is now consistently the real brand
              icon (BrandLogo falls back to the static /icon-192.png),
              and the name is shown on every viewport instead of being
              dropped on small screens. Truncation handles overflow on
              narrow phones. */}
          <BrandLogo size={compact ? 20 : 22} className="text-blue-700 shrink-0" />
          <span className="font-black tracking-tighter font-['Chivo'] truncate
                           text-[12px] sm:text-sm md:text-base
                           max-w-[160px] sm:max-w-[220px] md:max-w-none">
            {brandName}
          </span>
        </Link>
      )}

      {showScreenShare && (
        <Pill testid="header-brand-screenshare"
              label="Screen share" Icon={Monitor} color="emerald"
              onClick={() => navigate("/conversations-v2")} />
      )}
      {showCompanyBtn && (
        <Pill testid="header-brand-company"
              label={companyLabel} Icon={Buildings} color="slate"
              onClick={onCompanyClick} />
      )}

      {/* iter150AZ.49 — Universal cheat-sheet link. Renders on every
          page that uses HeaderBrand so reps can always pull up the
          printable Quick Start summary, regardless of which menu
          they're on. Pages that wire a full guided tour
          (CustomerDetail, PmViewer, QuoteDetail) still render their
          per-section "?" button next to that section's title. */}
      <UniversalCheatsheetLink />

      {picker && list.length > 1 && (
        <CompanyPickerModal
          companies={list}
          onPick={onPick}
          onClose={() => setPicker(false)}
        />
      )}
    </div>
  );
}


/**
 * UniversalCheatsheetLink — compact "?" pill in the page header. Opens
 * the language-aware Quick Start PDF (`/api/i18n/cheatsheet.pdf`) in a
 * new tab so reps can pull up the printable Help reference from any
 * menu without hunting for the per-section tour buttons.
 *
 * iter150AZ.49 — rendered by HeaderBrand on every non-auth route, so
 * every top-level menu (Customers, Compressors, Conversations,
 * Assistant, Logbook, Admin pages, etc.) gets the same Help
 * affordance as the Customer Detail panel.
 *
 * iter150AZ.64 — the pill is now SCREEN-AWARE. It detects which top-
 * level page the user is on (via the current pathname) and appends
 * ``?scope=...`` so the PDF renders ONLY that screen's cheat-sheet.
 * Operators complained that every Help button felt like "the customer
 * cheat-sheet" because Customer Detail was the first section of the
 * universal PDF — now each screen prints its own page.
 */
function _scopeForPathname(pathname) {
  const p = (pathname || "/").toLowerCase();
  // iter150AZ.65 — comprehensive route → scope mapping. Order
  // matters: more specific paths first.
  if (p.startsWith("/login")) return "";  // no Help on auth screens
  if (p === "/") return "landing";
  if (p === "/my-account" || p.startsWith("/my-account/")) return "my_account";
  if (p === "/my-access" || p.startsWith("/my-access/")) return "my_access";
  if (p.startsWith("/quote/")) return "quote_detail";
  if (p.startsWith("/my-prepared-quotes")) return "quote_detail";
  // /compressors/<serialId> → PM viewer modal; bare /compressors → list
  if (p.startsWith("/compressors/")) return "pm_viewer";
  if (p === "/compressors") return "compressors_list";
  if (p.startsWith("/customers")) return "customer_detail";
  if (p.startsWith("/conversations")) return "conversations";
  if (p.startsWith("/assistant")) return "assistant";
  if (p.startsWith("/logbook")) return "logbook";
  // Compressor catalogue (a.k.a. "Price List"). Four route variants
  // exist for US ↔ ACC × by-class ↔ by-HP. All four use CompressorPage.
  if (p.startsWith("/us-class") || p.startsWith("/us-hp")
      || p.startsWith("/acc-class") || p.startsWith("/acc-hp")) return "price_list";
  if (p.startsWith("/product-classes")) return "product_classes";
  if (p.startsWith("/checkout")) return "checkout";
  if (p.startsWith("/commissioning")) return "commissioning";
  if (p.startsWith("/service-tech")) return "service_tech";
  if (p.startsWith("/admin/pending-drafts")) return "admin";
  if (p.startsWith("/admin")) return "admin";
  if (p.startsWith("/warranty/claim") || p.startsWith("/warranty-claim")) return "warranty";
  if (p.startsWith("/warranty")) return "warranty";
  return "";  // fall back to universal PDF
}

function UniversalCheatsheetLink() {
  const t = useT();
  const { lang } = useI18n();
  const location = useLocation();
  const apiBase = process.env.REACT_APP_BACKEND_URL || "";
  const scope = _scopeForPathname(location?.pathname);
  const href =
    `${apiBase}/api/i18n/cheatsheet.pdf?lang=${encodeURIComponent(lang || "en")}`
    + (scope ? `&scope=${encodeURIComponent(scope)}` : "");
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      data-testid="header-brand-cheatsheet"
      data-scope={scope || "all"}
      title={t("tour.cheatsheet_link", "Print cheat-sheet")}
      className="inline-flex items-center gap-1 h-7 px-2 rounded-sm border text-[10px] font-bold uppercase tracking-wider bg-white text-blue-700 border-blue-200 hover:bg-blue-50 transition-colors shrink-0"
    >
      <Question size={12} weight="bold" />
      {/* iter150AZ.65 — show the "Help" label at every breakpoint
          (previously hidden below md). Operators were missing the
          icon-only pill on tablet/mobile. */}
      <span>{t("tour.help_label", "Help")}</span>
    </a>
  );
}

// iter150AZ.65 — exported so pages that don't render the full
// HeaderBrand (Commissioning, Checkout, Product Classes, Service-
// Tech, Admin dashboards, Quote Detail, Logbook, …) can still
// surface a Help pill that auto-detects scope from the current
// route.
export { UniversalCheatsheetLink as HelpPill };
